import {logger} from '../utils/logger';

const TOKEN_KEY = 'auth_token';
const REFRESH_TOKEN_KEY = 'auth_refresh_token';
const TOKEN_EXPIRY_KEY = 'auth_token_expiry';
export const TOKEN_REFRESH_WINDOW_MS = 60 * 1000;

export interface TokenInfo {
    token: string | null;
    refreshToken: string | null;
    expiry: number | null;
}

export interface TokenValidationResult {
    isValid: boolean;
    isExpired: boolean;
    needsRefresh: boolean;
}

class TokenProvider {
    getAccessToken(): string | null {
        try {
            return localStorage.getItem(TOKEN_KEY);
        } catch (error) {
            logger.error('Failed to get access token from localStorage:', error);
            return null;
        }
    }

    getRefreshToken(): string | null {
        try {
            return localStorage.getItem(REFRESH_TOKEN_KEY);
        } catch (error) {
            logger.error('Failed to get refresh token from localStorage:', error);
            return null;
        }
    }

    getTokenExpiry(): number | null {
        try {
            const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
            return expiry ? parseInt(expiry, 10) : null;
        } catch (error) {
            logger.error('Failed to get token expiry from localStorage:', error);
            return null;
        }
    }

    setAccessToken(token: string): void {
        try {
            localStorage.setItem(TOKEN_KEY, token);
        } catch (error) {
            logger.error('Failed to set access token in localStorage:', error);
            throw new Error('Failed to store access token');
        }
    }

    setRefreshToken(refreshToken: string): void {
        try {
            localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
        } catch (error) {
            logger.error('Failed to set refresh token in localStorage:', error);
            throw new Error('Failed to store refresh token');
        }
    }

    setTokenExpiry(expiry: number): void {
        try {
            localStorage.setItem(TOKEN_EXPIRY_KEY, expiry.toString());
        } catch (error) {
            logger.error('Failed to set token expiry in localStorage:', error);
            throw new Error('Failed to store token expiry');
        }
    }

    setTokens(token: string, refreshToken?: string, expiry?: number): void {
        this.setAccessToken(token);

        if (refreshToken) {
            this.setRefreshToken(refreshToken);
        }

        if (expiry) {
            this.setTokenExpiry(expiry);
        }
    }

    isTokenValid(token?: string): TokenValidationResult {
        const tokenToCheck = token || this.getAccessToken();

        if (!tokenToCheck) {
            return {
                isValid: false,
                isExpired: false,
                needsRefresh: false,
            };
        }

        const expiry = this.getTokenExpiry();
        const now = Date.now();

        if (!expiry) {
            return {
                isValid: true,
                isExpired: false,
                needsRefresh: false,
            };
        }

        const isExpired = now > expiry;
        const needsRefresh = !isExpired && (now > expiry - TOKEN_REFRESH_WINDOW_MS);

        return {
            isValid: !isExpired,
            isExpired,
            needsRefresh,
        };
    }

    isAuthenticated(): boolean {
        const validation = this.isTokenValid();
        return validation.isValid;
    }

    clearTokens(): void {
        try {
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(REFRESH_TOKEN_KEY);
            localStorage.removeItem(TOKEN_EXPIRY_KEY);
        } catch (error) {
            logger.error('Failed to clear tokens from localStorage:', error);
            throw new Error('Failed to clear tokens');
        }
    }

    getAuthHeader(): string | null {
        const token = this.getAccessToken();
        return token ? `Bearer ${token}` : null;
    }

    getTokenPayload(token?: string): any | null {
        try {
            const tokenToCheck = token || this.getAccessToken();
            if (!tokenToCheck) {
                return null;
            }

            const parts = tokenToCheck.split('.');
            if (parts.length !== 3) {
                return null;
            }

            const payload = parts[1];
            const decoded = atob(payload);
            return JSON.parse(decoded);
        } catch (error) {
            logger.error('Failed to extract token payload:', error);
            return null;
        }
    }

    getTokenExpirationFromJWT(token?: string): number | null {
        try {
            const payload = this.getTokenPayload(token);
            return payload?.exp ? payload.exp * 1000 : null;
        } catch (error) {
            logger.error('Failed to get token expiration from JWT:', error);
            return null;
        }
    }

    storeTokensFromCallback(token: string, refreshToken?: string): void {
        const expiry = this.getTokenExpirationFromJWT(token) || 0;

        this.setTokens(token, refreshToken, expiry);
    }

    updateTokensFromRefresh(token: string, expiresIn?: number, refreshToken?: string): void {
        const expiry = expiresIn ? Date.now() + (expiresIn * 1000) : this.getTokenExpirationFromJWT(token) || 0;

        this.setAccessToken(token);
        this.setTokenExpiry(expiry);
        if (refreshToken) {
            this.setRefreshToken(refreshToken);
        }
    }
}

export const tokenProvider = new TokenProvider();
export {TokenProvider};